/**
 * Task Detail Route (issue #1680, epic #1674)
 *
 * Shows a Task's full detail plus its respond flow: `TaskOutputForm` for a
 * single-respondent Task (`assigneeType` set), `TaskResponseForm` for a
 * multi-respondent Task (`audienceType` set) — mutually exclusive per
 * `assignable-tasks-spec.md`'s Assignee/Audience split.
 *
 * Also offers "Activate" for a `Draft` Task (issue #1829) — the deliberate
 * human checkpoint `assignable-tasks-spec.md` § "Scheduling and recurrence"
 * describes for a cadence-based or Staleness-triggered Task, which
 * (unlike an event-driven Task) has no domain event to serve as its own
 * activation signal. `isRespondable` only ever treats `Open`/`InProgress`
 * as respondable, so without this action a Draft Task created by either of
 * those two trigger kinds would be visible here but permanently stuck,
 * with no respond form and no way to progress it.
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ForgeButton, ForgeIcon } from '@tylertech/forge-react';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import { useTask } from '../../graphql/queries/task';
import { approveTask, activateTask, ApiError } from '../../lib/apiClient';
import { TaskOutputForm } from '../../components/tasks/TaskOutputForm';
import { TaskResponseForm } from '../../components/tasks/TaskResponseForm';
import { PermissionGate, EmptyState, DetailSkeleton } from '../../components/shared';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/features/auth/authSlice';
import type { Task } from '../../graphql/types/task';
import './taskId.css';

export const Route = createFileRoute('/tasks/$taskId')({
  component: TaskDetailPage,
});

export { TaskDetailPage };

const CardElement = 'forge-card' as React.ElementType;
const DividerElement = 'forge-divider' as React.ElementType;

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Task is still respondable (either output shape) — not yet Complete/Approved/Cancelled. */
function isRespondable(task: Task): boolean {
  return task.status === 'Open' || task.status === 'InProgress';
}

function TaskDetailPage(): JSX.Element {
  const { taskId } = Route.useParams();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const { data, loading, error, refetch } = useTask(taskId);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);

  const task = data?.task;

  if (loading) {
    return (
      <div className="task-detail-page">
        <DetailSkeleton cards={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="task-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load task"
          description={error.message}
          actionLabel="Back to My Tasks"
          onAction={() => void navigate({ to: '/tasks' })}
        />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="task-detail-page">
        <EmptyState
          icon="search_off"
          title="Task not found"
          description={`No task found with ID: ${taskId}`}
          actionLabel="Back to My Tasks"
          onAction={() => void navigate({ to: '/tasks' })}
        />
      </div>
    );
  }

  const handleApprove = async (): Promise<void> => {
    setApproving(true);
    setApproveError(null);
    try {
      await approveTask(task.entityId);
      await refetch();
    } catch (err) {
      setApproveError(err instanceof ApiError ? err.message : 'Failed to approve task');
    } finally {
      setApproving(false);
    }
  };

  const handleActivate = async (): Promise<void> => {
    setActivating(true);
    setActivateError(null);
    try {
      await activateTask(task.entityId);
      await refetch();
    } catch (err) {
      setActivateError(err instanceof ApiError ? err.message : 'Failed to activate task');
    } finally {
      setActivating(false);
    }
  };

  const canApprove = task.status === 'Complete' && !task.audienceType;
  // The deliberate human-activation checkpoint for a cadence/Staleness-
  // created Task (issue #1829) — every Task is created `Draft`
  // (`TaskRepository.createTask`), and nothing but this action ever moves
  // a cadence/Staleness Task out of it (unlike an event-driven Task, which
  // `createEventDrivenTask` opens automatically).
  const canActivate = task.status === 'Draft';

  return (
    <div className="task-detail-page">
      <div className="task-detail-header">
        <div>
          <ForgeButton type="button" variant="text" onClick={() => void navigate({ to: '/tasks' })}>
            <ForgeIcon name="arrow_back" slot="start" aria-hidden="true" />
            Back to My Tasks
          </ForgeButton>
          <h1 className="forge-typography--headline4">{task.name}</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {task.description || 'No description provided'}
          </p>
        </div>
        {canActivate && (
          <PermissionGate permission="task:update">
            <ForgeButton
              type="button"
              variant="raised"
              onClick={() => void handleActivate()}
              disabled={activating}
            >
              <ForgeIcon name="play_arrow" slot="start" aria-hidden="true" />
              {activating ? 'Activating…' : 'Activate'}
            </ForgeButton>
          </PermissionGate>
        )}
        {canApprove && (
          <PermissionGate permission="task:approve">
            <ForgeButton
              type="button"
              variant="raised"
              onClick={() => void handleApprove()}
              disabled={approving}
            >
              <ForgeIcon name="check" slot="start" aria-hidden="true" />
              {approving ? 'Approving…' : 'Approve'}
            </ForgeButton>
          </PermissionGate>
        )}
      </div>

      {task.overdue && (
        <div className="task-detail-banner task-detail-banner--overdue" role="alert">
          <ForgeIcon name="warning" aria-hidden="true" />
          This task is overdue — it was due {formatDate(task.dueDate)}.
        </div>
      )}

      {task.audienceType === 'EntityUsageCascade' && (
        <div className="task-detail-banner task-detail-banner--info" role="note">
          <ForgeIcon name="info" aria-hidden="true" />
          This task&apos;s audience was resolved once, when the task was created, from teams using{' '}
          {task.audienceCascadeEntityType ?? 'the affected entity'}{' '}
          {task.audienceCascadeEntityId ?? ''}. A team that starts or stops using it after this task
          was created is not added to or removed from this task — this is by design, not a bug.
        </div>
      )}

      {activateError && (
        <p className="forge-typography--body2 task-detail-error" role="alert">
          {activateError}
        </p>
      )}

      {approveError && (
        <p className="forge-typography--body2 task-detail-error" role="alert">
          {approveError}
        </p>
      )}

      <div className="task-detail-content">
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Overview</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Task type</span>
              <span className="detail-value">{task.taskType}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Subject</span>
              <span className="detail-value">
                {task.subjectEntityType} {task.subjectEntityId}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status</span>
              <span className="detail-value">{task.status}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Due date</span>
              <span className="detail-value">{formatDate(task.dueDate)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Assigned to</span>
              <span className="detail-value">
                {task.assigneeType
                  ? `${task.assigneeType} ${task.assigneeId}`
                  : `Audience (${task.audienceType})`}
              </span>
            </div>
          </div>
        </CardElement>

        {isRespondable(task) && task.audienceType && (
          <CardElement className="detail-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Respond</h2>
            </div>
            <DividerElement />
            <TaskResponseForm
              taskId={task.entityId}
              userTeams={user?.teams ?? []}
              currentUserId={user?.id ?? ''}
              onSubmitted={() => void refetch()}
            />
          </CardElement>
        )}

        {isRespondable(task) && !task.audienceType && (
          <PermissionGate
            permission="task:update"
            fallback={
              <CardElement className="detail-card">
                <div className="card-header">
                  <h2 className="forge-typography--headline6">Respond</h2>
                </div>
                <DividerElement />
                <p className="forge-typography--body2">
                  Being this task&apos;s assignee isn&apos;t enough to submit output — that also
                  requires one of a fixed set of editor roles (Administrator, Service Editor, Team
                  Administrator, Business Editor, or Capability Administrator). This is a known
                  platform limitation, not specific to this task, so no action on the task itself
                  will unblock it. Ask an administrator for one of those roles.
                </p>
              </CardElement>
            }
          >
            <CardElement className="detail-card">
              <div className="card-header">
                <h2 className="forge-typography--headline6">Respond</h2>
              </div>
              <DividerElement />
              <TaskOutputForm task={task} onSubmitted={() => void refetch()} />
            </CardElement>
          </PermissionGate>
        )}

        {!isRespondable(task) && (
          <CardElement className="detail-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Output</h2>
            </div>
            <DividerElement />
            {task.output ? (
              <div className="detail-grid">
                {Object.entries(task.output).map(([key, value]) => (
                  <div className="detail-row" key={key}>
                    <span className="detail-label">{key}</span>
                    <span className="detail-value">{String(value)}</span>
                  </div>
                ))}
              </div>
            ) : task.resultEntityId ? (
              <p className="forge-typography--body2">
                Linked result: {task.resultEntityType} {task.resultEntityId}
              </p>
            ) : (
              <p className="forge-typography--body2">No output recorded.</p>
            )}
          </CardElement>
        )}
      </div>
    </div>
  );
}
